// Tipos do módulo Financeiro (Conta Corrente, Cartões de Crédito, Lançamentos
// Recorrentes, Importação de Extrato, Plano de Contas e Margem de Produtos).
//
// Convenções do backend (base /api/v1, envelope {success, data}):
// - valores monetários: números simples;
// - datas: ISO `yyyy-MM-dd` ou timestamp ISO completo;
// - activityType: 0 = Saída, 1 = Entrada;
// - movement (plano de contas): 0 = Saída, 1 = Entrada, 2 = Outros.

// ─── Conta Corrente ─────────────────────────────────────────────

export interface FinancialAccount {
  accountId: number;
  companyId: number;
  idAccountsMain: number;
  accountsDescription: string;
  bankNumber: string | null;
  bankName: string | null;
  branchNumber: number | null;
  accountNumber: string | null;
  accountNumberDigit: number | null;
  viewIndex: number | null;
  currentBalance: number;
  // Preenchidos apenas na listagem do cadastro (Configuração → Conta Corrente).
  companyName?: string | null;
  companyDocument?: string | null;
  /** Integração de extrato bancário (Itaú) ativa e com certificado carregado —
   *  liga o modo "Conciliação Itaú" na tela de Conta Corrente. */
  bankIntegrationActive?: boolean;
}

/**
 * Monta o trecho "Ag 1234 / Cc 56789-0" a partir dos dados bancários da conta
 * (agência, número e dígito), omitindo as partes ausentes. Retorna '' quando
 * não há nenhuma informação.
 */
export const formatAccountBankInfo = (a: {
  branchNumber?: number | null;
  accountNumber?: string | null;
  accountNumberDigit?: number | null;
}): string => {
  const parts: string[] = [];
  if (a.branchNumber != null) parts.push(`Ag ${a.branchNumber}`);
  if (a.accountNumber) {
    parts.push(`Cc ${a.accountNumber}${a.accountNumberDigit != null ? `-${a.accountNumberDigit}` : ''}`);
  }
  return parts.join(' / ');
};

/** Uma parcela dentro da linha prevista agrupada por dia e bandeira. */
export interface ProjectedStatementItem {
  cardReceivableId: number;
  netAmount: number;
  /** "3/6"; vazio em parcela única. */
  installmentLabel?: string;
  orderId?: number | null;
  comercialProposalId?: number | null;
}

// ─── Rateio entre categorias ────────────────────────────────────
// Um lançamento (conta corrente ou item de fatura) pode ser dividido em duas
// ou mais categorias. O pai guarda a categoria PRINCIPAL (a maior parte) e o
// valor total; as partes vêm em `allocations`.

/** Parte do rateio, como vem da API. */
export interface FinancialAllocation {
  allocationId: number;
  lineOrder: number;
  accountingPlanErpId: number;
  categoryLabel: string | null;
  /** COM SINAL, igual ao pai (negativo = saída). */
  amount: number;
  note: string | null;
}

/**
 * Parte do rateio no request. `amount` é sempre POSITIVO — o sinal vem do
 * lançamento. Mínimo 2 partes; a soma em centavos precisa fechar o valor.
 */
export interface FinancialAllocationRequest {
  accountingPlanErpId: number;
  amount: number;
  note?: string | null;
}

export interface FinancialTransaction {
  transactionId: number;
  description: string;
  transactionDate: string;
  amount: number;
  /**
   * true = a linha NÃO é um lançamento: é uma parcela da agenda de recebíveis,
   * dinheiro que a adquirente ainda vai creditar. Nunca existiu na conta, não
   * pode ser editada nem excluída, e some sozinha quando o crédito é conciliado.
   */
  projected?: boolean;
  /**
   * Parcelas que compõem a linha prevista — o que a tela mostra ao expandir.
   * Vêm junto no extrato: ao contrário do depósito realizado, que busca as
   * vendas por endpoint próprio, aqui os dados já estão em memória.
   */
  projectedItems?: ProjectedStatementItem[];
  /**
   * Saldo acumulado incluindo as previsões. `runningBalance` continua sendo só o
   * dinheiro real — é ele que bate com o extrato do banco.
   */
  projectedBalance?: number;
  /** Previsto para data já passada e ainda não recebido. */
  overdue?: boolean;
  activityType: number;
  accountingPlanErpId: number | null;
  categoryLabel: string | null;
  idCreditCardsInvoices: number | null;
  transactionGroup: string | null;
  recurringTransactionId: number | null;
  /** Competência (AAAAMM) que gerou o lançamento recorrente. */
  recurringCompetence: number | null;
  /**
   * Lançamento de recorrência que alguém já ajustou à mão: o valor e a data
   * reais entraram no lugar da estimativa, e o processo diário não o
   * sobrescreve mais.
   */
  recurringLocked: boolean;
  /** Lançamento provisionado gerado das parcelas de um Pedido de Compra. */
  purchaseOrderId: number | null;
  /** Idem, de um Recebimento de Materiais. */
  materialReceiptId: number | null;
  /**
   * Provisão ainda do gerador (LastUpdateUser = system:provision): a
   * ressincronização do pedido pode apagá-la e recriá-la. Falso na provisão
   * editada por gente e no lançamento vinculado à mão ao pedido.
   */
  provisionGenerated: boolean;
  /** Fornecedor do pagamento, com ou sem pedido de compra vinculado. */
  providerId: number | null;
  providerName: string | null;
  runningBalance: number;
  /**
   * Preenchido quando o lançamento é um depósito de adquirente — é o que
   * habilita expandir a linha e ver as vendas que compõem aquele crédito.
   */
  cardSettlementId: number | null;
  /** Lançamento rateado entre categorias (categoryLabel é a principal). */
  allocated: boolean;
  /** Partes do rateio — só quando `allocated`. */
  allocations?: FinancialAllocation[];
}

export interface TransactionsStatement {
  openingBalance: number;
  closingBalance: number;
  transactions: FinancialTransaction[];
}

export interface TransactionCreatePayload {
  companyId: number;
  idAccountsMain: number;
  description: string;
  transactionDate: string;
  amount: number; // sempre positivo; o sinal é derivado do activityType
  activityType: number;
  accountingPlanErpId: number | null;
  installments?: number;
  providerId?: number | null;
  /**
   * Rateio entre categorias (mín. 2 partes; soma == amount). Com
   * `installments > 1` o backend replica proporcionalmente em cada parcela.
   * Quando presente, `accountingPlanErpId` é ignorado pelo backend (ele grava
   * a maior parte como principal).
   */
  allocations?: FinancialAllocationRequest[];
}

export interface TransactionUpdatePayload {
  description: string;
  transactionDate: string;
  amount: number;
  activityType: number;
  accountingPlanErpId: number | null;
  providerId?: number | null;
  /**
   * TRI-STATE: ausente = manter o rateio atual (o backend recusa se o valor
   * mudou e o lançamento era rateado); `[]` = remover; lista = substituir.
   * A tela sempre envia explicitamente.
   */
  allocations?: FinancialAllocationRequest[];
}

/** PATCH /financial/transactions/:id/supplier-link */
export interface TransactionSupplierLinkPayload {
  providerId: number | null;
  purchaseOrderId: number | null;
}

/** Escopo de edição/exclusão de lançamentos parcelados (transactionGroup). */
export type GroupScope = 'single' | 'group';

export interface CieloUploadResult {
  insertedCount: number;
}

// ─── Cartões de Crédito ─────────────────────────────────────────

export interface CreditCard {
  idCreditCards: number;
  companyId: number;
  description: string;
  billingDueDay: number;
  billingCycleDay: number;
  bankNumber: string | null;
  /** Conta (IdAccountsMain) debitada no pagamento da fatura. */
  idAccountsMain: number | null;
  /** Categoria (ErpId) da transação espelho da fatura. */
  accountingPlanErpId: number | null;
}

/** Corpo do cadastro de cartão (Configuração › Financeiro › Cartões de Crédito). */
export interface CreditCardFormData {
  companyId: number;
  description: string;
  billingDueDay: number | null;
  billingCycleDay: number | null;
  bankNumber: string | null;
  idAccountsMain: number | null;
  accountingPlanErpId: number | null;
}

export interface CreditCardInvoice {
  idCreditCardsInvoices: number;
  year: number;
  month: number;
  description: string;
  total: number;
}

export interface CreditCardInvoicesResponse {
  currentInvoiceId: number | null;
  invoices: CreditCardInvoice[];
}

export interface CreditCardTransaction {
  transactionId: number;
  description: string;
  invoiceDueDate: string;
  amount: number;
  activityType: number;
  accountingPlanErpId: number | null;
  categoryLabel: string | null;
  repeatIndex: number | null;
  repeatTotal: number | null;
  transactionGroup: string | null;
  recurringTransactionId: number | null;
  /**
   * Assinatura recorrente cujo valor real já foi ajustado nesta fatura — o
   * processo diário não a sobrescreve mais.
   */
  recurringLocked: boolean;
  /** Item rateado entre categorias (categoryLabel é a principal). */
  allocated: boolean;
  /** Partes do rateio — só quando `allocated`. */
  allocations?: FinancialAllocation[];
}

// ─── Projeção de parcelas ───────────────────────────────────────
// GET /financial/credit-cards/projection — matriz compra parcelada × competência.
// As parcelas futuras JÁ SÃO linhas gravadas nas faturas seguintes; o backend
// só agrupa por TransactionGroup e alinha ao eixo de meses (corrente → última
// competência com lançamento).

export interface CreditCardProjectionCard {
  idCreditCards: number;
  companyId: number | null;
  description: string;
}

export interface CreditCardProjectionGroup {
  key: string;
  /** Sem o sufixo " NN/MM". */
  description: string;
  /** Recorrência de cartão (assinatura/verba mensal) — linha própria, não vai para "demais". */
  recurring: boolean;
  idCreditCards: number;
  companyId: number | null;
  repeatTotal: number | null;
  /** Soma das parcelas dentro da janela (negativo = despesa). */
  total: number;
  /** Alinhado a months; 0 = sem parcela no mês. */
  monthAmounts: number[];
  /** "03/12" alinhado a months; "" = sem parcela. */
  monthLabels: string[];
}

/** "Demais lançamentos": só os avulsos — parcelas e recorrências têm linha própria. */
export interface CreditCardProjectionOther {
  idCreditCards: number;
  companyId: number | null;
  monthAmounts: number[];
}

export interface CreditCardProjection {
  /** Competências "yyyy-MM", da corrente à última com lançamento. */
  months: string[];
  cards: CreditCardProjectionCard[];
  groups: CreditCardProjectionGroup[];
  /** "Demais lançamentos" (avulsos + recorrências) por cartão. */
  others: CreditCardProjectionOther[];
}

export interface CreditCardInvoiceTransactionsResponse {
  total: number;
  transactions: CreditCardTransaction[];
}

export interface CreditCardTransactionCreatePayload {
  idCreditCardsInvoices: number;
  description: string;
  invoiceDueDate: string;
  amount: number;
  activityType: number;
  accountingPlanErpId: number | null;
  installments?: number;
  /** Rateio entre categorias — mesmas regras de TransactionCreatePayload. */
  allocations?: FinancialAllocationRequest[];
}

export interface CreditCardTransactionUpdatePayload {
  description: string;
  invoiceDueDate: string;
  amount: number;
  activityType: number;
  accountingPlanErpId: number | null;
  /** TRI-STATE — mesmas regras de TransactionUpdatePayload. */
  allocations?: FinancialAllocationRequest[];
}

export interface InvoiceUploadItem {
  description: string;
  invoiceDueDate: string;
  amount: number;
  repeatIndex: number | null;
  repeatTotal: number | null;
  suggestedAccountingPlanErpId: number | null;
}

export interface InvoiceUploadReview {
  new: InvoiceUploadItem[];
  found: InvoiceUploadItem[];
  missingFromFile: InvoiceUploadItem[];
}

export interface InvoiceUploadConfirmItem {
  description: string;
  invoiceDueDate: string;
  amount: number;
  repeatIndex: number | null;
  repeatTotal: number | null;
  accountingPlanErpId: number | null;
}

export interface InvoiceUploadConfirmResult {
  insertedCount: number;
  warnings: string[];
}

// ─── Lançamentos Recorrentes ────────────────────────────────────

/** Tipo do lançamento recorrente na tela: 1 = Conta Corrente, 2 = Cartão de Crédito. */
export type RecurringType = 1 | 2;

export interface RecurringTransaction {
  recurringTransactionId: number;
  companyId: number;
  description: string;
  transactionDay: number;
  amount: number;
  activityType: number;
  accountingPlanErpId: number | null;
  categoryLabel: string | null;
  idAccountsMain: number | null;
  idCreditCards: number | null;
}

export interface RecurringTransactionPayload {
  companyId: number;
  description: string;
  transactionDay: number;
  amount: number;
  activityType: number;
  accountingPlanErpId: number | null;
  idAccountsMain?: number | null;
  idCreditCards?: number | null;
}

/**
 * Modo de exclusão de recorrente:
 * 0 = somente a definição;
 * 1 = definição + lançamentos gerados a partir do mês atual;
 * 2 = definição + lançamentos gerados após o mês atual.
 */
export type RecurringDeleteMode = 0 | 1 | 2;

// ─── Importação de Extrato ──────────────────────────────────────

export interface StatementImportMissingRow {
  date: string;
  description: string;
  amount: number;
}

export interface StatementImportResult {
  insertedCount: number;
  missingFromFile: StatementImportMissingRow[];
}

export interface ImportCorrelation {
  transactionImportCorrelationId: number;
  companyId: number;
  description: string;
  accountingPlanErpId: number | null;
  categoryLabel: string | null;
  /** Sentido esperado do evento: null = ambos, 0 = Saída, 1 = Entrada. */
  movement: number | null;
  /** true = o job do extrato Itaú lança sozinho; false = só sugere na tela. */
  autoCreate: boolean;
  /** Escopo: 0 = ambos, 1 = só extrato bancário, 2 = só fatura de cartão. */
  appliesTo: number;
}

export interface ImportCorrelationPayload {
  companyId: number;
  description: string;
  accountingPlanErpId: number | null;
  movement: number | null;
  autoCreate: boolean;
  appliesTo: number;
}

// ─── Recebimentos PIX ───────────────────────────────────────────

export interface PixReceivable {
  pixReceivableId: number;
  pixPaymentId: number;
  companyId: number;
  ec?: number;
  orderId?: number;
  comercialProposalId?: number;
  txId: string;
  endToEndId?: string;
  /** Wall-clock Brasil "AAAA-MM-DDTHH:mm:ss". */
  paidAt?: string;
  grossAmountCents: number;
  /** Taxa PREVISTA (snapshot do cadastro) — informativa; a tarifa real é consolidada mensal. */
  feeAmountCents: number;
  feePercent: number;
  acquirerPixTaxId?: number;
  idAccountsMain?: number;
  financialTransactionId?: number;
  /** 0 = registrado; 1 = crédito confirmado pelo extrato do Itaú. */
  status: number;
  bankReconciledOn?: string;
}

export interface PixFeeSummary {
  companyId: number;
  month: string; // "AAAA-MM"
  pixCount: number;
  grossCents: number;
  expectedFeeCents: number;
  chargedFeeCents: number;
  chargedDate?: string; // "AAAA-MM-DD"
}

// ─── Plano de Contas ────────────────────────────────────────────

export interface AccountingPlanNode {
  accountingPlanId: number;
  erpId: number;
  description: string;
  movement: number; // 0 = Saída, 1 = Entrada, 2 = Outros
  level: number;
  parentErpId: number | null;
  deleted: boolean;
  disabled: boolean;
  dreGroupId: number | null;
  dreGroupCode: string | null;
  dreGroupName: string | null;
  inherited: boolean;
  children: AccountingPlanNode[];
}

export interface DreGroup {
  dreGroupId: number;
  code: string;
  name: string;
  displayOrder: number;
  nature: string | null;
  costBehavior: string | null;
  revenueStream: string | null;
  excludeFromDre: boolean;
}

export interface AccountingPlanCreatePayload {
  companyId: number;
  description: string;
  movement: number;
  parentErpId?: number | null;
}

export interface AccountingPlanUpdatePayload {
  description: string;
  movement: number;
  disabled: boolean;
}

// ─── Margem de Produtos ─────────────────────────────────────────

export interface ProductEarnRow {
  sku: string;
  description: string | null; // *string na API
  price: number;
  cost: number;
  stockQuantityAvailable: number | null; // *int na API — NULL em produto sem estoque cadastrado
  earn: number; // razão price/cost — exibida como percentual
}
